import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { track } from '@/lib/analytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Copy, Check, Share2, UserPlus, Shield, Stethoscope, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

interface InviteCompanyMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  companyName: string;
  canInviteCoordinators?: boolean;
}

type CompanyRole = 'coordinator' | 'employee';

interface GeneratedInvite {
  link: string;
  role: CompanyRole;
  email: string;
  copied: boolean;
}

type Step = 'select-role' | 'enter-email';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteCompanyMemberDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  canInviteCoordinators = true,
}: InviteCompanyMemberDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [invites, setInvites] = useState<GeneratedInvite[]>([]);
  const [step, setStep] = useState<Step>('select-role');
  const [selectedRole, setSelectedRole] = useState<CompanyRole | null>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const selectRole = (role: CompanyRole) => {
    setSelectedRole(role);
    setEmail('');
    setEmailError('');
    setStep('enter-email');
  };

  const generateInvite = async () => {
    if (!user || !selectedRole) return;

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setEmailError(t('company.invite.emailRequired'));
      return;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setEmailError(t('company.invite.emailInvalid'));
      return;
    }

    setIsGenerating(true);
    setEmailError('');

    try {
      const { data, error } = await supabase
        .rpc('create_company_invitation', {
          p_company_id: companyId,
          p_role: selectedRole,
          p_email: trimmed,
        })
        .single();

      if (error) throw error;

      const baseUrl = Capacitor.isNativePlatform()
        ? 'https://careos.life'
        : window.location.origin;
      const link = `${baseUrl}/invite/company/${(data as any).token}`;

      setInvites((prev) => [...prev, { link, role: selectedRole, email: trimmed, copied: false }]);
      track('company_member_invited', { company_id: companyId, role: selectedRole, channel: 'email' });
      setStep('select-role');
      setSelectedRole(null);
      setEmail('');

      await navigator.clipboard.writeText(link);
      toast.success(t('company.invite.linkCopied'));

      setInvites((prev) =>
        prev.map((inv) =>
          inv.link === link ? { ...inv, copied: true } : inv
        )
      );

      setTimeout(() => {
        setInvites((prev) =>
          prev.map((inv) =>
            inv.link === link ? { ...inv, copied: false } : inv
          )
        );
      }, 3000);
    } catch (error) {
      console.error('Error generating company invite:', error);
      toast.error(t('company.invite.error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const copyLink = async (link: string) => {
    await navigator.clipboard.writeText(link);
    toast.success(t('company.invite.linkCopied'));

    setInvites((prev) =>
      prev.map((inv) =>
        inv.link === link ? { ...inv, copied: true } : inv
      )
    );

    setTimeout(() => {
      setInvites((prev) =>
        prev.map((inv) =>
          inv.link === link ? { ...inv, copied: false } : inv
        )
      );
    }, 3000);
  };

  const shareViaWhatsApp = (link: string, role: CompanyRole) => {
    const roleLabel =
      role === 'coordinator'
        ? t('company.invite.roleCoordinator')
        : t('company.invite.roleCaregiver');

    const message = t('company.invite.whatsappMessage', {
      role: roleLabel,
      company: companyName,
      link,
    });

    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setInvites([]);
      setStep('select-role');
      setSelectedRole(null);
      setEmail('');
      setEmailError('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {t('company.invite.title')}
          </DialogTitle>
          <DialogDescription>
            {t('company.invite.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {step === 'select-role' && (
            <div className={`grid grid-cols-1 gap-3 ${canInviteCoordinators ? 'sm:grid-cols-2' : 'max-w-xs mx-auto'}`}>
              {canInviteCoordinators && (
                <Button
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => selectRole('coordinator')}
                  disabled={isGenerating}
                >
                  <Shield className="h-6 w-6 text-primary" />
                  <span className="text-sm font-medium">
                    {t('company.invite.roleCoordinator')}
                  </span>
                  <span className="text-xs text-muted-foreground text-center">
                    {t('company.invite.coordinatorDesc')}
                  </span>
                </Button>
              )}

              <Button
                variant="outline"
                className="h-auto py-4 flex flex-col items-center gap-2"
                onClick={() => selectRole('employee')}
                disabled={isGenerating}
              >
                <Stethoscope className="h-6 w-6 text-primary" />
                <span className="text-sm font-medium">
                  {t('company.invite.roleCaregiver')}
                </span>
                <span className="text-xs text-muted-foreground text-center">
                  {t('company.invite.caregiverDesc')}
                </span>
              </Button>
            </div>
          )}

          {step === 'enter-email' && (
            <div className="space-y-4">
              <button
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                onClick={() => { setStep('select-role'); setEmailError(''); }}
              >
                <ArrowLeft className="h-4 w-4" />
                {t('common.back')}
              </button>

              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {selectedRole === 'coordinator'
                    ? t('company.invite.roleCoordinator')
                    : t('company.invite.roleCaregiver')}
                </Badge>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite-email">{t('company.invite.emailLabel')}</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder={t('company.invite.emailPlaceholder')}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && generateInvite()}
                />
                {emailError && (
                  <p className="text-xs text-destructive">{emailError}</p>
                )}
              </div>

              <Button
                className="w-full"
                onClick={generateInvite}
                disabled={isGenerating}
              >
                {t('company.invite.generateLink')}
              </Button>
            </div>
          )}

          {invites.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                {t('company.invite.generatedLinks')}
              </p>

              {invites.map((invite, idx) => (
                <div
                  key={idx}
                  className="w-full rounded-lg border bg-accent/50 p-3 overflow-hidden"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1 space-y-1 overflow-hidden">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="secondary" className="text-xs w-fit">
                          {invite.role === 'coordinator'
                            ? t('company.invite.roleCoordinator')
                            : t('company.invite.roleCaregiver')}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{invite.email}</span>
                      </div>

                      <p className="max-w-full break-all text-xs leading-relaxed text-muted-foreground whitespace-normal">
                        {invite.link}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => copyLink(invite.link)}
                      >
                        {invite.copied ? (
                          <Check className="h-4 w-4 text-primary" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>

                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => shareViaWhatsApp(invite.link, invite.role)}
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
